/**
 * Erasing what this device holds (DPDP Phase 9).
 *
 * Three ways in:
 *  - a WIPE_LOCAL_DATA command from Device Management (lib/deviceCommands.ts);
 *  - the server answering DEVICE_DECOMMISSIONED - a decommissioned till
 *    erases itself the next time it reaches the server;
 *  - "Remove offline data from this browser" in the account menu, for a
 *    browser that was set up for offline billing.
 *
 * What goes: the local SQLite database (every file in the OPFS pool - bills,
 * orders, the staff list, offline PIN verifiers and grants, sessions), the
 * device credential and identity, the offline-mode flags, session storage,
 * and on the Android shell its cookies, web storage, HTTP cache and native
 * secret store. What stays: the app itself (service-worker shell cache) and
 * display preferences (theme, paper width) - nothing personal.
 *
 * Unsynced work would be lost, so nobody erases with it still here unawares:
 * an admin's wipe command and decommissioning first send it up on the
 * device's own credential (lib/finalSync.ts) and erase only once none is
 * left; the account-menu path refuses while any is pending unless the
 * person confirms.
 */
import { clearNativeWebData } from './androidBridge';
import { clearDeviceCredential } from './deviceCredential';
import { closePosDataSource, wipeLocalDatabaseFile } from './posData';

/** localStorage keys that identify this device or carry its offline state. */
const DEVICE_KEYS = [
  'kortex.deviceId',
  'kortex.offlineModeEnabled',
  'kortex.posDataMode',
  'kortex.deviceAccess',
];

export async function wipeThisDevice(): Promise<{ databaseErased: boolean }> {
  await closePosDataSource();
  let databaseErased = true;
  try {
    await wipeLocalDatabaseFile();
  } catch (error) {
    // Another tab may still hold the database open. Everything else is still
    // cleared, and the native clear below (on the kiosk) removes the files anyway.
    databaseErased = false;
    console.warn('[wipe] could not erase the local database file', error);
  }
  clearDeviceCredential();
  for (const key of DEVICE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // storage unavailable - nothing to clear
    }
  }
  try {
    window.sessionStorage.clear();
  } catch {
    // storage unavailable
  }
  // Last: on the kiosk this also removes the web storage the page is using.
  const native = await clearNativeWebData('all');
  return { databaseErased: databaseErased || native.ok };
}
